"use client";

import { useEffect, useState, useCallback } from "react";

export interface GpuQuotaWindow {
  used: number;
  limit: number;
  remaining: number;
  resetInSeconds: number | null;
}

export interface UsageData {
  usage: {
    minute: { used: number; limit: number | null; remaining: number | null };
    hour: { used: number; limit: number | null; remaining: number | null };
  };
  gpuQuota?: {
    fourHour?: GpuQuotaWindow;
    sevenDay?: GpuQuotaWindow;
    monthly?: GpuQuotaWindow;
  };
  isAdmin: boolean;
  tier: "starter" | "plus" | "max" | "ultra";
}

/** Safely extract a numeric value from gpuQuota entry (handles both raw number and { used: number }) */
export function getGpuSeconds(entry: number | { used: number } | undefined): number {
  if (entry == null) return 0;
  if (typeof entry === "number") return entry;
  return entry.used ?? 0;
}

export const TIER_LABELS: Record<string, string> = {
  starter: "Starter",
  plus: "Plus",
  max: "Max",
  ultra: "Ultra",
};

/**
 * Fetches /api/user/usage at a given poll interval and returns the parsed data.
 * Returns null data and error=false while loading.
 * Silently handles fetch errors by setting error=true.
 */
export function useUserUsage(pollIntervalMs: number = 15_000) {
  const [data, setData] = useState<UsageData | null>(null);
  const [dataUpdatedAt, setDataUpdatedAt] = useState<number | null>(null);
  const [error, setError] = useState(false);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await fetch("/api/user/usage", { credentials: "include" });
      if (!res.ok) {
        setError(true);
        return;
      }
      const json = await res.json();
      // Validate expected shape before setting state
      if (json?.usage?.hour && json?.tier) {
        setData(json);
        setDataUpdatedAt(Date.now());
        setError(false);
      }
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch("/api/user/usage", { credentials: "include" });
        if (cancelled) return;
        if (!res.ok) {
          setError(true);
          return;
        }
        const json = await res.json();
        if (cancelled) return;
        if (json?.usage?.hour && json?.tier) {
          setData(json);
          setDataUpdatedAt(Date.now());
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };

    void poll();
    const interval = setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [pollIntervalMs]);

  return { data, dataUpdatedAt, error, refetch: fetchUsage };
}
